<!-- ══════════════════════════════════════════════════════════════════════════
     ALTA DE UN USUARIO DEL COMERCIO — página con ruta propia, a todo el ancho.

     🔴 REDISEÑO 2026-09-20 (contrato del rediseño).

     Lo que salió, y por qué:
       · las TRES tarjetas — envolvían campos de formulario, que no se tocan ni
         llevan a otro lado. Pasaron a `.kb-seccion`. Las dos únicas cajas que
         quedan son las que SÍ se tocan: los botones de elegir el tipo.
       · los TRES subtítulos. El único que decía algo que el campo no decía —la
         clave inicial que el sistema va a exigir cambiar— bajó al renglón de
         ayuda del nombre de usuario, pegado al dato que describe.
       · los dos pies de tarjeta con los mismos dos botones, duplicados uno en
         cada camino. Quedó UNA barra pegada al pie.

     Los rótulos que cambiaron. Un título de sección es un SUSTANTIVO, nunca una
     pregunta: una pregunta se lee bien la primera vez y deja de servir apenas
     hay que nombrarla en una reunión o buscarla.
       · «¿Qué vas a dar de alta?» → «Tipo de usuario»
       · «Quién es»                → «Datos de la persona»  (nombre, apellido, correo)
       · «Con qué entra»           → «Nombre de usuario»    (es el único campo que hay)
       · «Qué puesto de cobro»     → «Tienda y caja»        (los dos selectores, en ese orden)
       · «Con qué se abre»         → «Usuario y clave»      (usuario, clave y su repetición)

     🔴 El espaciado agrupa, y son TRES distancias que se tienen que notar:
        · 0.375rem  dentro de un campo   (rótulo → casilla → renglón de ayuda)
        · 1.25rem   entre campos         (gap-5)
        · 2rem      entre secciones      (space-y-8)
     ══════════════════════════════════════════════════════════════════════════ -->
<div class="kb-page">

  <!-- ══ Migas — de dónde se viene ════════════════════════════════════════════ -->
  <nav class="kb-migas" aria-label="Ubicación">
    <a class="kb-miga" [routerLink]="['/console/merchants']">Comercios</a>
    <span class="kb-miga-sep" aria-hidden="true"><i class="fas fa-chevron-right"></i></span>
    <a class="kb-miga" [routerLink]="['/console/merchants', codigo]">{{ comercio?.name || codigo }}</a>
    <span class="kb-miga-sep" aria-hidden="true"><i class="fas fa-chevron-right"></i></span>
    <a class="kb-miga" [routerLink]="['/console/merchants', codigo, 'usuarios']">Usuarios</a>
    <span class="kb-miga-sep" aria-hidden="true"><i class="fas fa-chevron-right"></i></span>
    <span class="kb-miga-actual">Nuevo usuario</span>
  </nav>

  <!-- ══ Cargando ═════════════════════════════════════════════════════════════ -->
  <div *ngIf="cargando" class="space-y-8" aria-live="polite" aria-busy="true">
    <div class="kb-skeleton h-9 w-1/3"></div>
    <div class="space-y-4">
      <div class="kb-skeleton h-3 w-28"></div>
      <div class="kb-skeleton h-10 w-full max-w-xl" *ngFor="let s of [1,2]"></div>
    </div>
  </div>

  <!-- ══ No existe ════════════════════════════════════════════════════════════ -->
  <div class="kb-empty" *ngIf="!cargando && noEncontrado">
    <span class="kb-empty-icono"><i class="fas fa-store-slash" aria-hidden="true"></i></span>
    <p class="kb-empty-titulo">No encontramos el comercio «{{ codigo }}»</p>
    <p class="kb-empty-texto">Puede que el código esté mal escrito.</p>
    <a class="kb-btn kb-btn-outline mt-4" [routerLink]="['/console/merchants']">
      <i class="fas fa-arrow-left" aria-hidden="true"></i>Volver a comercios
    </a>
  </div>

  <ng-container *ngIf="!cargando && !noEncontrado">

    <!-- ══ Encabezado ═════════════════════════════════════════════════════════ -->
    <div class="kb-ficha">
      <div class="min-w-0">
        <h1 class="kb-ficha-titulo truncate">Nuevo usuario</h1>
        <p class="kb-ficha-meta">
          <span class="truncate">Entra al portal de {{ comercio?.name || codigo }}</span>
          <span aria-hidden="true">·</span>
          <span class="font-mono">{{ codigo }}</span>
        </p>
      </div>

      <div class="flex flex-wrap items-center gap-2 sm:shrink-0">
        <button type="button" class="kb-btn kb-btn-outline" (click)="cancelar()"
                [disabled]="guardando">
          <i class="fas fa-arrow-left" aria-hidden="true"></i>Volver a usuarios
        </button>
      </div>
    </div>

    <div class="space-y-8">

      <!-- ══ Tipo de usuario ══════════════════════════════════════════════════
           🔴 Primero el tipo. Los dos caminos casi no comparten campos, y un
           formulario único con la mitad apagada es el que termina creando cajas
           con el correo de quien las creó. -->
      <section class="kb-tarjeta">
        <div class="kb-tarjeta-cab">
          <h2 class="text-base font-semibold leading-none">Tipo de usuario</h2>
        </div>
        <div class="kb-tarjeta-cuerpo">

        <!-- Las dos cajas que se quedan: se tocan y cambian lo que sigue. -->
        <div class="grid gap-4 sm:grid-cols-2">
          <button type="button"
                  class="flex items-start gap-3 rounded-lg border p-4 text-left transition-colors"
                  [ngClass]="tipo === 'PERSONA'
                             ? 'border-primary ring-1 ring-primary bg-background'
                             : 'border-border bg-background hover:bg-accent'"
                  [attr.aria-pressed]="tipo === 'PERSONA'"
                  (click)="elegirTipo('PERSONA')">
            <i class="fas fa-user mt-0.5 text-muted-foreground" aria-hidden="true"></i>
            <span>
              <span class="block text-sm font-medium text-foreground">Persona</span>
              <span class="mt-1 block text-xs text-muted-foreground">
                Gente del comercio: administrador, gerente o ejecutivo de ventas.
              </span>
            </span>
          </button>
          <button type="button"
                  class="flex items-start gap-3 rounded-lg border p-4 text-left transition-colors"
                  [ngClass]="tipo === 'CAJA'
                             ? 'border-primary ring-1 ring-primary bg-background'
                             : 'border-border bg-background hover:bg-accent'"
                  [attr.aria-pressed]="tipo === 'CAJA'"
                  (click)="elegirTipo('CAJA')">
            <i class="fas fa-cash-register mt-0.5 text-muted-foreground" aria-hidden="true"></i>
            <span>
              <span class="block text-sm font-medium text-foreground">Caja</span>
              <span class="mt-1 block text-xs text-muted-foreground">
                El acceso que opera un puesto de cobro de una tienda. No es una persona:
                no lleva correo.
              </span>
            </span>
          </button>
        </div>
        </div>
      </section>

      <!-- ══ PERSONA ══════════════════════════════════════════════════════════ -->
      <ng-container *ngIf="tipo === 'PERSONA'">

        <!-- 🔴 El papel (2026-10-08): antes toda persona nacía administrador. -->
        <section class="kb-tarjeta">
          <div class="kb-tarjeta-cab">
            <h2 class="text-base font-semibold leading-none">Rol</h2>
          </div>
          <div class="kb-tarjeta-cuerpo">
          <div class="grid gap-4" [ngClass]="rolesPersona.length === 4 ? 'sm:grid-cols-2 lg:grid-cols-4' : rolesPersona.length === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'">
            <button type="button" *ngFor="let r of rolesPersona"
                    class="flex items-start gap-3 rounded-lg border p-4 text-left transition-colors"
                    [ngClass]="rolPersona === r.valor
                               ? 'border-primary ring-1 ring-primary bg-background'
                               : 'border-border bg-background hover:bg-accent'"
                    [attr.aria-pressed]="rolPersona === r.valor"
                    (click)="rolPersona = r.valor">
              <span>
                <span class="block text-sm font-medium text-foreground">{{ r.titulo }}</span>
                <span class="mt-1 block text-xs text-muted-foreground">{{ r.detalle }}</span>
              </span>
            </button>
          </div>
          </div>
        </section>

        <section class="kb-tarjeta">
          <div class="kb-tarjeta-cab">
            <h2 class="text-base font-semibold leading-none">Datos de la persona</h2>
          </div>
          <div class="kb-tarjeta-cuerpo">

          <div class="grid gap-5 sm:grid-cols-2">
            <div>
              <label class="kb-label" for="usrNombre">Nombre <span class="text-destructive">*</span></label>
              <input id="usrNombre" type="text" class="kb-input" [(ngModel)]="form.name"
                     (ngModelChange)="alEscribirNombre()" placeholder="Angela" autocomplete="off">
            </div>
            <div>
              <label class="kb-label" for="usrApellido">Apellido</label>
              <input id="usrApellido" type="text" class="kb-input" [(ngModel)]="form.lastName"
                     placeholder="González" autocomplete="off">
            </div>
          </div>

          <div class="sm:max-w-md">
            <label class="kb-label" for="usrMail">Correo <span class="text-destructive">*</span></label>
            <input id="usrMail" type="email" class="kb-input" [(ngModel)]="form.email"
                   placeholder="angela@comercio.com" autocomplete="off">
          </div>
          </div>
        </section>

        <!-- El nombre de usuario, con el acrónimo del comercio fijo adelante.
             Va fijo a propósito: viendo un nombre suelto en un log o un ticket
             tiene que saberse de qué comercio es sin ir a buscarlo. -->
        <section class="kb-tarjeta">
          <div class="kb-tarjeta-cab">
            <h2 class="text-base font-semibold leading-none">Nombre de usuario</h2>
          </div>
          <div class="kb-tarjeta-cuerpo">

          <div class="sm:max-w-md">
            <label class="kb-label" for="usrNombreUsuario">Nombre de usuario</label>
            <div class="flex items-stretch">
              <span class="inline-flex items-center rounded-l-md border border-r-0 border-input
                           bg-muted px-3 text-sm text-muted-foreground">
                {{ prefijo }}.
              </span>
              <input id="usrNombreUsuario" type="text" class="kb-input rounded-l-none"
                     [(ngModel)]="form.sufijo" (ngModelChange)="normalizarSufijo()"
                     placeholder="angela" autocomplete="off">
            </div>
            <!-- El renglón que se salvó del recorte: la clave inicial y que el
                 sistema le va a exigir cambiarla. Va pegado al dato, no debajo
                 de un título. -->
            <p class="kb-hint" *ngIf="usuarioFinal && !usuarioRepetido">
              Va a entrar como <strong>{{ usuarioFinal }}</strong>, con una clave inicial que el
              sistema le va a exigir cambiar.
            </p>
            <p class="kb-error" *ngIf="usuarioRepetido">
              Ya existe <strong>{{ usuarioFinal }}</strong> en este comercio. Elige otro.
            </p>
            <p class="kb-hint" *ngIf="!usuarioFinal">
              Se arma solo con el nombre. Si lo dejas vacío, el primero del comercio queda como
              <strong>{{ prefijo }}.portal</strong>.
            </p>
            <!-- ⚠️ Si la lista no vino, no se afirma que el nombre está libre. -->
            <p class="kb-hint" *ngIf="!listaDisponible">
              Todavía no se puede saber si ese nombre ya está tomado: no se pudo traer la lista
              de usuarios de este comercio. El servidor lo va a rechazar si está repetido.
            </p>
          </div>
          </div>
        </section>
      </ng-container>

      <!-- ══ CAJA ═════════════════════════════════════════════════════════════
           🔴 Acá NO se crea la caja: se crea el USUARIO que va a operar una caja
           que ya existe. La caja es una entidad de la tienda —número, afiliación
           y tipo— y se da de alta en la ficha de la tienda. Lo que viaja
           estampado en la venta es esa caja, no el nombre del usuario. -->
      <ng-container *ngIf="tipo === 'CAJA'">

        <div class="kb-aviso kb-aviso-alerta" *ngIf="!tiendasParaCaja.length">
          <i class="fas fa-exclamation-triangle mt-0.5" aria-hidden="true"></i>
          <div>
            <p class="kb-aviso-titulo">Este comercio no tiene tiendas habilitadas</p>
            <!-- 🔴 Tiendas dejó de ser una pestaña de la ficha el 2026-09-20: es una
                 pantalla con ruta propia. Un texto que manda a un lugar que ya no
                 existe deja al que lo lee buscando algo que no va a encontrar — y
                 aquí, además, el enlace le ahorra el camino de vuelta. -->
            <p>Una caja vive en una tienda. Crea o habilita la tienda y vuelve aquí.</p>
            <a class="kb-btn kb-btn-outline kb-btn-sm mt-3"
               [routerLink]="['/console/merchants', codigo, 'tiendas']">
              <i class="fas fa-store" aria-hidden="true"></i>Ir a las tiendas
            </a>
          </div>
        </div>

        <ng-container *ngIf="tiendasParaCaja.length">

          <section class="kb-tarjeta">
            <div class="kb-tarjeta-cab">
              <h2 class="text-base font-semibold leading-none">Tienda y caja</h2>
            </div>
            <div class="kb-tarjeta-cuerpo">

            <div class="grid gap-5 sm:grid-cols-2">
              <div>
                <label class="kb-label" for="cajaTienda">
                  Tienda <span class="text-destructive">*</span>
                </label>
                <div class="kb-select-envoltorio">
                  <select id="cajaTienda" class="kb-select" [(ngModel)]="form.shop"
                          (ngModelChange)="alElegirTienda()">
                    <option value="">Elige la tienda</option>
                    <option *ngFor="let t of tiendasParaCaja" [value]="t.code">
                      {{ t.code }} · {{ t.name || t.short_name }}
                    </option>
                  </select>
                  <i class="fas fa-chevron-down kb-select-flecha" aria-hidden="true"></i>
                </div>
              </div>
              <div>
                <label class="kb-label" for="cajaElegida">
                  Caja <span class="text-destructive">*</span>
                </label>
                <div class="kb-select-envoltorio">
                  <select id="cajaElegida" class="kb-select" [(ngModel)]="form.cajaId"
                          [disabled]="!form.shop || cargandoCajasTienda || !cajasDeLaTienda.length">
                    <option value="">
                      {{ !form.shop ? 'Elige primero la tienda'
                                    : (cargandoCajasTienda ? 'Buscando las cajas…' : 'Elige la caja') }}
                    </option>
                    <option *ngFor="let c of cajasDeLaTienda" [value]="c._id">
                      {{ etiquetaCaja(c) }}
                    </option>
                  </select>
                  <i class="fas fa-chevron-down kb-select-flecha" aria-hidden="true"></i>
                </div>
                <!-- ⚠️ Un error del servidor no se muestra como «no tiene cajas». -->
                <p class="kb-error" *ngIf="errorCajasTienda">{{ errorCajasTienda }}</p>
                <p class="kb-hint" *ngIf="form.shop && !errorCajasTienda && cajasDeLaTienda.length">
                  La caja ya tiene que existir en la tienda: se elige una de las que hay y el
                  número no se teclea. Aquí sólo se le da la credencial con la que se abre.
                </p>
              </div>
            </div>

            <!-- La tienda no tiene cajas: se dice, y se ofrece ir a crearla. -->
            <div class="kb-aviso kb-aviso-alerta" *ngIf="tiendaSinCajas">
              <i class="fas fa-exclamation-triangle mt-0.5" aria-hidden="true"></i>
              <div>
                <p class="kb-aviso-titulo">Esta tienda todavía no tiene cajas</p>
                <p>
                  La caja es una entidad de la tienda: se crea en su ficha, con su número,
                  su afiliación y su tipo. Créala allá y vuelve aquí a darle el usuario.
                </p>
                <button type="button" class="kb-btn kb-btn-outline kb-btn-sm mt-3"
                        (click)="irALaTienda(form.shop)">
                  <i class="fas fa-store" aria-hidden="true"></i>Ir a la tienda a crear la caja
                </button>
              </div>
            </div>
            </div>
          </section>

          <!-- El usuario lo escribe quien da de alta; el prefijo va fijo. -->
          <section class="kb-tarjeta">
            <div class="kb-tarjeta-cab">
              <h2 class="text-base font-semibold leading-none">Usuario y clave</h2>
            </div>
            <div class="kb-tarjeta-cuerpo">

            <div class="sm:max-w-md">
              <label class="kb-label" for="cajaUsuario">Usuario <span class="text-destructive">*</span></label>
              <div class="flex items-stretch">
                <span class="inline-flex items-center rounded-l-md border border-r-0 border-input
                             bg-muted px-3 text-sm text-muted-foreground">
                  {{ prefijo }}.
                </span>
                <input id="cajaUsuario" type="text" class="kb-input rounded-l-none"
                       [(ngModel)]="form.sufijo" (ngModelChange)="normalizarSufijo()"
                       placeholder="caja1" autocomplete="off">
              </div>
              <p class="kb-hint" *ngIf="usuarioFinal && !usuarioRepetido">
                Va a entrar como <strong>{{ usuarioFinal }}</strong>
              </p>
              <p class="kb-error" *ngIf="usuarioRepetido">
                Ya existe <strong>{{ usuarioFinal }}</strong> en este comercio. Elige otro.
              </p>
              <p class="kb-hint" *ngIf="!usuarioFinal">
                El código del comercio va adelante y no se edita: así se sabe de qué comercio
                es el usuario sin ir a buscarlo.
              </p>
              <p class="kb-hint" *ngIf="!listaDisponible">
                Todavía no se puede saber si ese nombre ya está tomado: no se pudo traer la lista
                de usuarios de este comercio.
              </p>
            </div>

            <!-- 🔴 La clave la escribe quien da de alta. Se pide dos veces porque
                 se dicta por teléfono, y un error de tipeo se descubriría recién
                 cuando el de la tienda no pueda entrar. -->
            <div class="grid gap-5 sm:grid-cols-2">
              <div>
                <label class="kb-label" for="cajaClave">Clave <span class="text-destructive">*</span></label>
                <div class="flex items-stretch">
                  <input id="cajaClave" class="kb-input rounded-r-none"
                         [type]="form.verClave ? 'text' : 'password'"
                         [(ngModel)]="form.clave" autocomplete="new-password">
                  <button type="button"
                          class="inline-flex items-center rounded-r-md border border-l-0 border-input
                                 bg-muted px-3 text-sm text-muted-foreground hover:text-foreground"
                          (click)="form.verClave = !form.verClave"
                          [attr.aria-label]="form.verClave ? 'Ocultar la clave' : 'Ver la clave'">
                    <i class="fas" [ngClass]="form.verClave ? 'fa-eye-slash' : 'fa-eye'"
                       aria-hidden="true"></i>
                  </button>
                </div>
                <p class="kb-hint" *ngIf="!form.clave || claveValida">Seis caracteres o más.</p>
                <p class="kb-error" *ngIf="form.clave && !claveValida">
                  Muy corta: seis caracteres o más.
                </p>
              </div>
              <div>
                <label class="kb-label" for="cajaClave2">
                  Repetir la clave <span class="text-destructive">*</span>
                </label>
                <input id="cajaClave2" class="kb-input"
                       [type]="form.verClave ? 'text' : 'password'"
                       [(ngModel)]="form.clave2" autocomplete="new-password">
                <p class="kb-hint" *ngIf="!form.clave2 || claveConfirmada">
                  Se pide dos veces porque se dicta por teléfono.
                </p>
                <p class="kb-error" *ngIf="form.clave2 && !claveConfirmada">
                  Las dos claves no son iguales.
                </p>
              </div>
            </div>
            </div>
          </section>

        </ng-container>
      </ng-container>
    </div>

    <!-- ══ Barra de acciones, pegada al pie ═══════════════════════════════════
         Una sola: los dos caminos tenían su propio pie con los mismos dos
         botones. -->
    <div class="kb-barra-pie" *ngIf="tipo">
      <p class="kb-barra-nota">
        <span class="kb-td-alerta font-medium" *ngIf="!valido">
          <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>{{ faltaParaCrear }}
        </span>
        <span *ngIf="valido">
          {{ tipo === 'CAJA'
             ? 'Listo: entra con la clave que le pusiste.'
             : 'Listo: entra con una clave inicial que va a tener que cambiar.' }}
        </span>
      </p>
      <div class="flex flex-wrap items-center justify-end gap-2">
        <button type="button" class="kb-btn kb-btn-outline"
                [disabled]="guardando" (click)="cancelar()">Cancelar</button>
        <button type="button" class="kb-btn kb-btn-primary"
                [disabled]="!valido || guardando" (click)="crear()">
          <i class="fas" [ngClass]="guardando ? 'fa-spinner fa-spin' : 'fa-check'" aria-hidden="true"></i>
          {{ guardando ? 'Creando…' : (tipo === 'CAJA' ? 'Crear el usuario de la caja' : 'Crear el usuario') }}
        </button>
      </div>
    </div>

  </ng-container>
</div>
